import { useState, useEffect } from 'react';
import { Users, Mail, Send, Eye, MousePointer, TrendingUp, Package, Truck, FileText, DollarSign } from 'lucide-react';
import { adminAxios } from './AdminLayout';

export default function AdminExecutive() {
  var [data, setData] = useState(null);
  var [period, setPeriod] = useState(null);
  var [loading, setLoading] = useState(true);

  function fetchData(days) {
    var url = '/api/admin/executive-dashboard';
    if (days) url += '?days=' + days;
    adminAxios().get(url).then(function(res) { setData(res.data); setLoading(false); }).catch(function() { setLoading(false); });
  }
  useEffect(function() { fetchData(period); }, [period]);

  if (loading) return <div className="animate-pulse font-manrope text-slate-500">Loading executive dashboard...</div>;
  if (!data) return <div className="font-manrope text-slate-500">Dashboard unavailable.</div>;

  var a = data.analytics || {};
  var p = data.pipeline || {};

  return (
    <div data-testid="admin-executive-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-outfit text-2xl font-bold text-slate-900">Executive Overview</h2>
        <div className="flex gap-2">
          {[{d:7,l:'7d'},{d:30,l:'30d'},{d:90,l:'90d'},{d:null,l:'All'}].map(function(pr) {
            return <button key={String(pr.d)} onClick={function(){setPeriod(pr.d);}} className={'px-3 py-1.5 rounded-full font-manrope text-xs font-medium ' + (period === pr.d ? 'bg-[#0096D6] text-white' : 'bg-slate-100 text-slate-600')}>{pr.l}</button>;
          })}
        </div>
      </div>

      {/* Top metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {[
          {v: data.clients.total, l: 'Total Clients', icon: Users, c: 'bg-blue-500'},
          {v: data.clients.subscribed, l: 'Subscribed', icon: Mail, c: 'bg-green-500'},
          {v: data.offers.sent, l: 'Offers Sent', icon: Send, c: 'bg-cyan-500'},
          {v: data.rates.open_rate + '%', l: 'Open Rate', icon: Eye, c: 'bg-emerald-500'},
          {v: data.rates.click_rate + '%', l: 'Click Rate', icon: MousePointer, c: 'bg-purple-500'},
        ].map(function(card, i) {
          return <div key={i} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className={'w-9 h-9 rounded-lg flex items-center justify-center ' + card.c}><card.icon className="w-4 h-4 text-white" /></div>
              <div><p className="font-space text-xl font-bold text-slate-900">{card.v}</p><p className="font-manrope text-[10px] text-slate-500">{card.l}</p></div>
            </div>
          </div>;
        })}
      </div>

      {/* Pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4">Sales Pipeline</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-blue-50 rounded-xl text-center"><p className="font-space text-2xl font-bold text-blue-600">{p.total_requests || 0}</p><p className="font-manrope text-[10px] text-blue-600">Quote Requests</p></div>
            <div className="p-3 bg-orange-50 rounded-xl text-center"><p className="font-space text-2xl font-bold text-orange-600">{p.negotiating || 0}</p><p className="font-manrope text-[10px] text-orange-600">Negotiating</p></div>
            <div className="p-3 bg-green-50 rounded-xl text-center"><p className="font-space text-2xl font-bold text-green-600">{p.won || 0}</p><p className="font-manrope text-[10px] text-green-600">Won</p></div>
            <div className="p-3 bg-green-50 rounded-xl text-center"><p className="font-space text-2xl font-bold text-green-600">{p.conversion_rate || 0}%</p><p className="font-manrope text-[10px] text-green-600">Conversion Rate</p></div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4">Platform Stats</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl"><Package className="w-5 h-5 text-[#0096D6]" /><div><p className="font-space text-lg font-bold text-slate-900">{data.offers.total}</p><p className="font-manrope text-[10px] text-slate-500">Total Offers</p></div></div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl"><FileText className="w-5 h-5 text-[#0096D6]" /><div><p className="font-space text-lg font-bold text-slate-900">{data.product_db_count}</p><p className="font-manrope text-[10px] text-slate-500">Product DB</p></div></div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl"><Truck className="w-5 h-5 text-[#0096D6]" /><div><p className="font-space text-lg font-bold text-slate-900">{data.active_suppliers}</p><p className="font-manrope text-[10px] text-slate-500">Active Suppliers</p></div></div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl"><DollarSign className="w-5 h-5 text-green-600" /><div><p className="font-space text-lg font-bold text-green-600">{Math.round(p.won_value || 0).toLocaleString()}</p><p className="font-manrope text-[10px] text-slate-500">Won Value</p></div></div>
          </div>
        </div>
      </div>

      {/* Top offers */}
      {(data.top_offers || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4">Top Performing Offers</h3>
          <div className="space-y-2">
            {data.top_offers.map(function(o, i) {
              return <div key={i} className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-xl">
                <span className="font-manrope text-sm text-slate-900">{o.subject}</span>
                <span className="font-space text-sm font-bold text-green-600">{o.opens} opens</span>
              </div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}
